import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, CubeIcon, PrinterIcon, TrashIcon, PencilIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { productService, Product } from '../../services/productService';
import toast from 'react-hot-toast';
import { useZebraPrinter } from '../../hooks/useZebraPrinter';
import { generateProductLabelZPL } from '../../utils/zplUtils';
import { InvoiceModal } from './InvoiceModal'; // Import InvoiceModal
import { ProductEntryModal } from './ProductEntryModal';
import { CorrectPreOrderModal } from './CorrectPreOrderModal';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types';

interface InvoiceDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    invoiceId: string | null;
    date?: string | null;
    receivedDate?: string | null;
    onUpdate?: () => void;
}

export const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({ isOpen, onClose, invoiceId, date, receivedDate, onUpdate }) => {
    const { user } = useAuth();
    const isAdmin = user?.rol_usr === UserRole.ADMIN;
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const { 
        printLabel, 
        loading: printing,
        isUSBConnected,
        connectUSB,
        usbDeviceName,
        error: printerError
    } = useZebraPrinter();
    const [isPrinting, setIsPrinting] = useState(false);
    const [printingItemIds, setPrintingItemIds] = useState<Set<number>>(new Set()); // New state for individual printing
    const [productToDelete, setProductToDelete] = useState<Product | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Switch Inventory State
    const [productToSwitch, setProductToSwitch] = useState<Product | null>(null);
    const [isSwitching, setIsSwitching] = useState(false);

    const [showAddModal, setShowAddModal] = useState(false);
    const [productToEdit, setProductToEdit] = useState<Product | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCorrectModal, setShowCorrectModal] = useState(false);

    useEffect(() => {
        if (isOpen && invoiceId) {
            loadProducts();
        } else {
            setProducts([]);
        }
    }, [isOpen, invoiceId]);

    const loadProducts = async () => {
        if (!invoiceId) return;
        setLoading(true);
        try {
            // Fetch all products for this invoice (limit high to get all)
            const response = await productService.getProducts({
                invoiceId,
                limit: 1000,
                type: 'all', // Support both types
                _t: Date.now() // Cache-buster to force fresh data
            });
            if (response.success) {
                setProducts(response.data);
            }
        } catch (error) {
            console.error('Error loading invoice details:', error);
            toast.error('Error al cargar detalles del invoice');
        } finally {
            setLoading(false);
        }
    };

    const handlePrintAllLabels = async () => {
        if (products.length === 0) return;
        setIsPrinting(true);
        try {
            // 1. Check for missing QRs and trigger generation if needed
            const missingQR = products.filter(p => !p.qr_code_value);
            if (missingQR.length > 0) {
                toast.loading(`Generando ${missingQR.length} códigos QR faltantes...`, { id: 'generating-qr' });
                // Generate in parallel
                await Promise.all(missingQR.map(p =>
                    productService.updateProduct(p.id_producto, { generate_qr: true, type: p.inventoryType })
                ));
                toast.dismiss('generating-qr');
                toast.success(`Códigos generados. Preparando impresión...`);
                // Reload products to get new QRs? ideally yes, but we can't easily wait for reload. 
                // We'll trust backend or should re-fetch. 
                // Let's re-fetch quickly or just assume update worked? Re-fetching is safer.
                await loadProducts();
            }

            // Refetch products to ensure we have latest QRs if we just generated them
            // Actually, loadProducts updates state, but async state updates... 
            // Better to re-fetch and use the response directly? 
            // For simplicity, let's call loadProducts and wait, assuming it updates 'products' state but we need access to the NEW products here.
            // Actually, let's just re-fetch locally to a variable to be safe.
            const response = await productService.getProducts({
                invoiceId: invoiceId!,
                limit: 1000,
                type: 'all'
            });
            const latestProducts = response.data;


            let totalZpl = '';

            // Group unique codes to avoid redundant calls
            const uniqueCodes = Array.from(new Set(latestProducts.map((p: Product) => p.cod_producto).filter(Boolean))) as string[];
            const codeVariantsMap: Record<string, { sizes: string[], colors: string[] }> = {};

            await Promise.all(uniqueCodes.map(async (code) => {
                codeVariantsMap[code] = await productService.getVariants(code);
            }));

            latestProducts.forEach((p: Product) => {
                const variants = p.cod_producto ? codeVariantsMap[p.cod_producto] : { sizes: [], colors: [] };
                totalZpl += generateProductLabelZPL(p, {
                    noteNumber: p.saleFolio,
                    availableSizes: variants?.sizes || [],
                    availableColors: variants?.colors || []
                });
            });

            if (!totalZpl) { toast.error('No hay etiquetas para imprimir'); return; }

            const success = await printLabel(totalZpl);
            if (success) {
                toast.success('Enviado a imprimir ' + latestProducts.length + ' etiquetas');
            } else {
                toast.error(printerError || 'Error al imprimir etiquetas. Verifica conexión USB.');
            }
        } catch (error) {
            console.error(error);
            toast.error('Error al generar etiquetas');
        } finally {
            setIsPrinting(false);
        }
    };

    const handlePrintSingleLabel = async (product: Product) => {
        setPrintingItemIds(prev => new Set(prev).add(product.id_producto));
        try {
            let currentProduct = product;

            // 1. Check for missing QR and trigger generation if needed
            if (!currentProduct.qr_code_value) {
                toast.loading(`Generando código QR...`, { id: `generating-qr-${product.id_producto}` });
                await productService.updateProduct(currentProduct.id_producto, { generate_qr: true, type: currentProduct.inventoryType });
                toast.dismiss(`generating-qr-${product.id_producto}`);

                // Re-fetch to get the updated product with QR
                const response = await productService.getProducts({ invoiceId: invoiceId!, limit: 1000, type: 'all' });
                const updatedProduct = response.data.find(p => p.id_producto === currentProduct.id_producto);
                if (updatedProduct) {
                    currentProduct = updatedProduct;
                }
            }

            // 2. Fetch variants
            const variants = currentProduct.cod_producto ? await productService.getVariants(currentProduct.cod_producto) : { sizes: [], colors: [] };

            // 3. Generate ZPL
            const zpl = generateProductLabelZPL(currentProduct, {
                noteNumber: currentProduct.saleFolio,
                availableSizes: variants?.sizes || [],
                availableColors: variants?.colors || []
            });

            if (!zpl) { toast.error('Error al generar etiqueta'); return; }

            // 4. Print
            const success = await printLabel(zpl);
            if (success) {
                toast.success('Etiqueta enviada a imprimir');
            } else {
                toast.error(printerError || 'Error al imprimir etiqueta. Verifica conexión USB.');
            }
        } catch (error) {
            console.error(error);
            toast.error('Error al imprimir etiqueta indiviual');
        } finally {
            setPrintingItemIds(prev => {
                const next = new Set(prev);
                next.delete(product.id_producto);
                return next;
            });
        }
    };

    const handleDeleteClick = (product: Product) => {
        // Validation: Block deletion if product is tied to a sale or is a pre-order with tracking
        // (If product.pre_order is true, it implies it's associated with a client/sale, 
        //  we also check saleFolio just in case it's a standard sold item)
        if (product.pre_order || product.saleFolio) {
            toast.error(
                'No se puede eliminar este producto porque tiene una venta o reservación (Pre-order) asociada.\nSi fue un error de captura, cancela la venta primero.',
                { duration: 5000 }
            );
            return;
        }
        setProductToDelete(product);
    };

    const handleEditClick = (product: Product) => {
        setProductToEdit(product);
        setShowEditModal(true);
    };

    const confirmDelete = async () => {
        if (!productToDelete) return;
        setIsDeleting(true);
        toast.loading('Eliminando producto...', { id: 'delete-product' });
        try {
            await productService.deleteProduct(productToDelete.id_producto, productToDelete.inventoryType);
            toast.dismiss('delete-product');
            toast.success('Producto eliminado de la base de datos');
            setProductToDelete(null);

            // Re-fetch products locally to update stats and list
            await loadProducts();
            if (onUpdate) onUpdate();
        } catch (error: any) {
            console.error('Error deleting product:', error);
            toast.dismiss('delete-product');
            toast.error(error.response?.data?.message || 'Error al eliminar el producto');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSwitchClick = (product: Product) => {
        setProductToSwitch(product);
    };

    const confirmSwitch = async () => {
        if (!productToSwitch) return;
        setIsSwitching(true);
        const targetType = productToSwitch.inventoryType === 'maestro' ? 'novias' : 'maestro';
        toast.loading(`Moviendo a inventario ${targetType.toUpperCase()}...`, { id: 'switch-inventory' });
        try {
            await productService.switchInventoryType(productToSwitch.id_producto, targetType);
            toast.dismiss('switch-inventory');
            toast.success(`Producto movido a ${targetType.toUpperCase()}`);
            setProductToSwitch(null);
            await loadProducts();
        } catch (error: any) {
            console.error('Error switching product:', error);
            toast.dismiss('switch-inventory');
            toast.error(error.response?.data?.message || 'Error al cambiar inventario');
        } finally {
            setIsSwitching(false);
        }
    };

    if (!isOpen || !invoiceId) return null;

    // Calculate totals
    const totalItems = products.length;
    const totalValue = products.reduce((acc, p) => acc + (Number(p.precio) || 0), 0);
    const inTransitCount = products.filter(p => p.in_transit).length;

    return createPortal(
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[1000] flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden animate-fade-in-up" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="bg-white border-b border-gray-100 p-6 flex justify-between items-center">
                    <div className="flex items-center space-x-4">
                        <div className="bg-indigo-50 p-3 rounded-xl text-indigo-600">
                            <CubeIcon className="w-8 h-8" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Detalle de Invoice</h2>
                            <div className="flex flex-col">
                                <span className="text-gray-500 font-mono text-sm">{invoiceId}</span>
                                <div className="flex gap-4 text-xs mt-1">
                                    {date && (
                                        <span className="text-gray-500">
                                            Creado: <span className="font-medium text-gray-700">{new Date(date).toLocaleDateString()}</span>
                                        </span>
                                    )}
                                    {receivedDate && (
                                        <span className="text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                                            Recibido: <span className="font-medium">{new Date(receivedDate).toLocaleDateString()} {new Date(receivedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {isAdmin && (
                            <>
                                <button
                                    onClick={() => setShowCorrectModal(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors shadow-sm font-medium mr-2"
                                    title="Herramienta para vincular Pre-orders huérfanos con este Invoice"
                                >
                                    <WrenchScrewdriverIcon className="w-5 h-5" />
                                    Corregir Pre-orders
                                </button>
                                <button
                                    onClick={() => setShowAddModal(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                                >
                                    <span className="text-xl leading-none">+</span>
                                    Agregar productos
                                </button>
                                <button
                                    onClick={connectUSB}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                                        isUSBConnected 
                                        ? 'bg-green-50 border-green-500 text-green-700' 
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                    title={isUSBConnected ? `Conectado a ${usbDeviceName}` : 'Configurar Impresora Zebra via USB'}
                                >
                                    <PrinterIcon className="w-5 h-5" />
                                    <span>{isUSBConnected ? 'USB Listo' : 'Configurar USB'}</span>
                                </button>
                                <button
                                    onClick={handlePrintAllLabels}
                                    disabled={isPrinting || printing}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                                >
                                    <PrinterIcon className="w-5 h-5" />
                                    {isPrinting ? 'Procesando...' : 'Imprimir Etiquetas'}
                                </button>
                            </>
                        )}
                        <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
                            <XMarkIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Stats Bar */}
                <div className="bg-gray-50 px-8 py-4 border-b border-gray-100 grid grid-cols-4 gap-4">
                    <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <span className="text-xs text-gray-500 uppercase font-bold tracking-wider">Total Productos</span>
                        <div className="text-xl font-bold text-gray-900">{totalItems}</div>
                    </div>
                    {isAdmin && (
                        <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                            <span className="text-xs text-gray-500 uppercase font-bold tracking-wider">Valor Estimado</span>
                            <div className="text-xl font-bold text-gray-900">${totalValue.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</div>
                        </div>
                    )}
                    <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <span className="text-xs text-gray-500 uppercase font-bold tracking-wider">En Tránsito</span>
                        <div className="text-xl font-bold text-amber-600">{inTransitCount}</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <span className="text-xs text-gray-500 uppercase font-bold tracking-wider">Recibidos</span>
                        <div className="text-xl font-bold text-green-600">{totalItems - inTransitCount}</div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto bg-gray-50/50 p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                        </div>
                    ) : products.length === 0 ? (
                        <div className="text-center py-20 text-gray-400">
                            No se encontraron productos en este invoice.
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-10">#</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Código</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Marca</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Color</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Talla</th>
                                        {isAdmin && <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Precio</th>}
                                        <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Estado</th>
                                        {isAdmin && <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Acciones</th>}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {products.map((p, index) => (
                                        <tr key={p.id_producto} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-400">
                                                {index + 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-bold text-gray-900">{p.cod_producto || p.qr_code_value}</div>
                                                <div className="text-[10px] text-gray-400 font-mono mt-0.5">{p.inventoryType === 'novias' ? 'NOVIA' : 'GRAL'}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {p.marca}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {p.color}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {p.talla}
                                            </td>
                                            {isAdmin && (
                                                <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900">
                                                    ${Number(p.precio).toLocaleString('es-MX')}
                                                </td>
                                            )}
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <div className="flex flex-col gap-1 items-center">
                                                    {/* Status Badge */}
                                                    {p.in_transit ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                                            En Tránsito
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                            Recibido
                                                        </span>
                                                    )}

                                                    {/* Stock/Sold Badge */}
                                                    {p.pre_order ? (
                                                        <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200">
                                                            PRE-ORDER
                                                        </span>
                                                    ) : !p.in_transit && (
                                                        <span className="text-[10px] text-gray-500">
                                                            Stock: {p.stock}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            {isAdmin && (
                                                <td className="px-6 py-4 whitespace-nowrap text-center space-x-1 flex justify-center">
                                                    <button
                                                        onClick={() => handleSwitchClick(p)}
                                                        disabled={printingItemIds.has(p.id_producto) || isPrinting || printing || isSwitching}
                                                        className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed group relative"
                                                        title={`Mover a ${p.inventoryType === 'maestro' ? 'NOVIAS' : 'GENERAL'}`}
                                                    >
                                                        <ArrowsRightLeftIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                                    </button>
                                                    <button
                                                        onClick={() => handlePrintSingleLabel(p)}
                                                        disabled={printingItemIds.has(p.id_producto) || isPrinting || printing}
                                                        className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed group relative"
                                                        title="Imprimir Etiqueta"
                                                    >
                                                        {printingItemIds.has(p.id_producto) ? (
                                                            <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                                                        ) : (
                                                            <PrinterIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditClick(p)}
                                                        disabled={printingItemIds.has(p.id_producto) || isPrinting || printing || isDeleting}
                                                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed group relative"
                                                        title="Editar Producto"
                                                    >
                                                        <PencilIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(p)}
                                                        disabled={printingItemIds.has(p.id_producto) || isPrinting || printing || isDeleting}
                                                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed group relative"
                                                        title="Eliminar Producto"
                                                    >
                                                        <TrashIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-white border-t border-gray-100 p-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="bg-gray-800 text-white px-6 py-2.5 rounded-lg hover:bg-gray-900 transition-colors font-medium shadow-lg hover:shadow-xl active:scale-95"
                    >
                        Cerrar
                    </button>
                </div>
            </div>

            {showAddModal && (
                <InvoiceModal
                    isOpen={showAddModal}
                    onClose={() => setShowAddModal(false)}
                    onSuccess={() => {
                        setShowAddModal(false);
                        loadProducts();
                    }}
                    initialInvoiceId={invoiceId!}
                    initialBrand={products.length > 0 ? products[0].marca || undefined : undefined}
                />
            )}

            {
                showEditModal && productToEdit && (
                    <ProductEntryModal
                        isOpen={showEditModal}
                        onClose={() => {
                            setShowEditModal(false);
                            setProductToEdit(null);
                        }}
                        onSuccess={() => {
                            setShowEditModal(false);
                            setProductToEdit(null);
                            loadProducts(); // Recargar después de edición
                        }}
                        productToEdit={productToEdit}
                        initialInventoryType={productToEdit.inventoryType}
                    />
                )
            }

            {
                showCorrectModal && (
                    <CorrectPreOrderModal
                        isOpen={showCorrectModal}
                        onClose={() => setShowCorrectModal(false)}
                        invoiceId={invoiceId!}
                        onSuccess={() => {
                            setShowCorrectModal(false);
                            loadProducts();
                            if (onUpdate) onUpdate();
                        }}
                    />
                )
            }

            {/* Switch Inventory Confirmation Modal */}
            {
                productToSwitch && (
                    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => !isSwitching && setProductToSwitch(null)} />
                        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col overflow-hidden relative animate-bounce-in">
                            <div className="p-6">
                                <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mb-4 mx-auto">
                                    <ArrowsRightLeftIcon className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                                    ¿Cambiar de Inventario?
                                </h3>
                                <p className="text-gray-600 text-center text-sm mb-6">
                                    El producto <strong>{productToSwitch.cod_producto}</strong> pertenece actualmente a <strong>{productToSwitch.inventoryType?.toUpperCase()}</strong>.<br /><br />
                                    ¿Estás seguro que deseas moverlo a <strong>{productToSwitch.inventoryType === 'maestro' ? 'NOVIAS' : 'GENERAL'}</strong>?
                                </p>
                                <div className="flex gap-3 mt-6">
                                    <button
                                        onClick={() => setProductToSwitch(null)}
                                        disabled={isSwitching}
                                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={confirmSwitch}
                                        disabled={isSwitching}
                                        className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors shadow-md disabled:opacity-50 flex justify-center items-center"
                                    >
                                        {isSwitching ? 'Cambiando...' : 'Sí, Cambiar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Delete Confirmation Modal */}
            {
                productToDelete && (
                    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => !isDeleting && setProductToDelete(null)} />
                        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden relative animate-bounce-in">
                            <div className="bg-red-50 border-b border-red-100 p-5 flex items-start gap-4">
                                <div className="bg-red-100 p-2 rounded-full text-red-600 flex-shrink-0 mt-1">
                                    <TrashIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-red-900">Confirmar Eliminación</h3>
                                    <p className="text-sm text-red-700 mt-1">
                                        Esta acción eliminará permanentemente el producto de la base de datos. Si fue capturado por error, verifica las características:
                                    </p>
                                </div>
                            </div>

                            <div className="p-6 bg-gray-50 border-b border-gray-100">
                                <ul className="space-y-3">
                                    <li className="flex justify-between items-center text-sm">
                                        <span className="text-gray-500 font-medium">Código:</span>
                                        <span className="font-bold text-gray-900">{productToDelete.cod_producto || productToDelete.qr_code_value || 'N/A'}</span>
                                    </li>
                                    <li className="flex justify-between items-center text-sm">
                                        <span className="text-gray-500 font-medium">Marca:</span>
                                        <span className="text-gray-900 font-medium">{productToDelete.marca || 'N/A'}</span>
                                    </li>
                                    <li className="flex justify-between items-center text-sm">
                                        <span className="text-gray-500 font-medium">Color:</span>
                                        <span className="text-gray-900 font-medium">{productToDelete.color || 'N/A'}</span>
                                    </li>
                                    <li className="flex justify-between items-center text-sm">
                                        <span className="text-gray-500 font-medium">Talla:</span>
                                        <span className="text-gray-900 font-medium">{productToDelete.talla || 'N/A'}</span>
                                    </li>
                                    <li className="flex justify-between items-center text-sm pt-2 border-t border-gray-200 mt-2">
                                        <span className="text-gray-500 font-bold">Precio:</span>
                                        <span className="text-green-700 font-bold">${Number(productToDelete.precio).toLocaleString('es-MX')}</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="p-5 flex justify-end gap-3 bg-white">
                                <button
                                    onClick={() => setProductToDelete(null)}
                                    disabled={isDeleting}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 font-medium rounded-lg transition-colors disabled:opacity-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    disabled={isDeleting}
                                    className="px-5 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 active:scale-95 transition-all shadow-md disabled:opacity-50 flex items-center gap-2"
                                >
                                    {isDeleting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Borrando...
                                        </>
                                    ) : (
                                        'Sí, Eliminar Permanentemente'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >,
        document.body
    );
};
